import { supabase } from "@/integrations/supabase/client";
import type { Reservation, WaitlistEntry, UnavailablePeriod } from "@/types/supabase";
import { scheduleAutomation } from "@/services/automationService";

export const getReservations = async () => {
  const { data, error } = await supabase
    .from('reservations')
    .select('*')
    .is('deleted_at', null)
    .order('check_in', { ascending: true });
    
  if (error) throw error;
  return data as Reservation[];
};

export const getAllReservations = async (includeDeleted: boolean = true) => {
  let query = supabase
    .from('reservations')
    .select('*');
  
  if (!includeDeleted) {
    query = query.is('deleted_at', null);
  }
  
  const { data, error } = await query.order('check_in', { ascending: true });
    
  if (error) throw error;
  return data as Reservation[];
};

export const softDeleteReservation = async (id: string) => {
  const { error } = await supabase
    .from('reservations')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
    
  if (error) throw error;
  return true;
};

export const restoreReservation = async (id: string) => {
  const { error } = await supabase
    .from('reservations')
    .update({ deleted_at: null })
    .eq('id', id);
    
  if (error) throw error;
  return true;
};

export const updateReservationInfo = async (
  id: string, 
  info: { first_name?: string; last_name?: string; email?: string; phone?: string }
) => {
  const { data, error } = await supabase
    .from('reservations')
    .update(info)
    .eq('id', id)
    .select()
    .single();
    
  if (error) throw error;
  return data as Reservation;
};

export const getUserReservations = async (email: string) => {
  const normalizedEmail = email.trim().toLowerCase();
  const { data, error } = await supabase
    .from('reservations')
    .select('*')
    .or(`email.eq.${normalizedEmail},email.eq.${email}`)
    .is('deleted_at', null)
    .order('check_in', { ascending: true });
    
  if (error) throw error;
  return data as Reservation[];
};

export const createReservation = async (reservation: Omit<Reservation, 'id' | 'created_at'>) => {
  const { data, error } = await supabase
    .from('reservations')
    .insert([{ ...reservation, status: 'new' }])
    .select()
    .single();
    
  if (error) throw error;

  // Send push notification to admins
  try {
    await supabase.functions.invoke('send-push-notification', {
      body: {
        title: '🏠 Nouvelle demande de réservation',
        body: `${reservation.first_name} ${reservation.last_name} veut réserver`,
        url: '/admin',
        tag: 'reservation_request',
        notification_type: 'reservation_request'
      }
    });
  } catch (notifError) {
    console.error("Error sending push notification:", notifError);
  }

  // Schedule automations for reservation_created event
  scheduleAutomation({
    event: 'reservation_created',
    targetType: 'reservation',
    targetId: data.id,
  });

  return data as Reservation;
};

export const updateReservationStatus = async (id: string, status: Reservation['status']) => {
  const { data, error } = await supabase
    .from('reservations')
    .update({ status })
    .eq('id', id)
    .select()
    .single();
    
  if (error) throw error;

  // Schedule automations when reservation is confirmed
  if (status === 'confirmed') {
    scheduleAutomation({
      event: 'reservation_confirmed',
      targetType: 'reservation',
      targetId: id,
    });
  }

  return data as Reservation;
};

export const getUnavailablePeriods = async () => {
  const { data, error } = await supabase
    .from('unavailable_periods')
    .select('*')
    .order('start_date', { ascending: true });
    
  if (error) throw error;
  return data as UnavailablePeriod[];
};

export const addUnavailablePeriod = async (period: Omit<UnavailablePeriod, 'id' | 'created_at'>) => {
  const { data, error } = await supabase
    .from('unavailable_periods')
    .insert([period])
    .select()
    .single();
    
  if (error) throw error;
  return data as UnavailablePeriod;
};

export const removeUnavailablePeriod = async (id: string) => {
  const { error } = await supabase
    .from('unavailable_periods')
    .delete()
    .eq('id', id);
    
  if (error) throw error;
  return true;
};

export const addToWaitlist = async (entry: Omit<WaitlistEntry, 'id' | 'created_at'>) => {
  const { data, error } = await supabase
    .from('waitlist')
    .insert([entry])
    .select()
    .single();
    
  if (error) throw error;
  return data as WaitlistEntry;
};

export const getWaitlistEntries = async () => {
  const { data, error } = await supabase
    .from('waitlist')
    .select('*')
    .order('created_at', { ascending: false });
    
  if (error) throw error;
  return data as WaitlistEntry[];
};
